import Layout from '@eduzz/ui-layout';
import { BetaModule } from 'components/BetaModule';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { VERTICAL_URL } from 'settings';

import { Permission } from './Permission';

import type { Route } from './routes';

const { Sidebar } = Layout;
const { Group, Item } = Sidebar;

function serializeMenuItemLabel(label: string) {
  return label.toLowerCase().replace(/\s+/g, '-');
}

type SubMenuRouteProps = {
  route: Route;
  children: any[];
};

function SubMenuRoute({ children, route }: SubMenuRouteProps) {
  const isEmpty = React.Children.map(children, (child) => child?.type(child.props)).length === 0;

  if (isEmpty) return null;

  return (
    <Group id={route.label ? `menu-group-${serializeMenuItemLabel(route.label)}` : ''} label={route.label}>
      {children}
    </Group>
  );
}

type MenuRouteProps = {
  route: Route;
};

const MenuRoute = React.memo(({ route }: MenuRouteProps) => {
  const isSubmenu = route.nodes?.length > 0;

  if (isSubmenu) {
    return (
      <SubMenuRoute route={route}>
        {route.nodes.map((node) => (
          <Permission key={node.to} route={node}>
            <BetaModule module={node.betaModule}>
              {node.to.startsWith('http') ? (
                <Item
                  id={`menu-item-${serializeMenuItemLabel(route.label)}-${serializeMenuItemLabel(node.label)}`}
                  as='a'
                  href={node.to}
                  target={node.to.includes(VERTICAL_URL) ? '_self' : '_blank'}
                  rel='noopener noreferrer'
                >
                  {node.label}
                </Item>
              ) : (
                <Item id={`menu-item-${serializeMenuItemLabel(route.label)}-${serializeMenuItemLabel(node.label)}`} as={NavLink} to={node.to}>
                  {node.label}
                </Item>
              )}
            </BetaModule>
          </Permission>
        ))}
      </SubMenuRoute>
    );
  }

  if (route.external) {
    return (
      <Permission route={route}>
        <BetaModule module={route.betaModule}>
          <Item
            id={`menu-item-${serializeMenuItemLabel(route.label)}`}
            as={NavLink}
            to={{ pathname: route.to }}
            target='_blank'
            rel='noopener noreferrer'
            icon={route.icon || undefined}
          >
            {route.label}
          </Item>
        </BetaModule>
      </Permission>
    );
  }

  return (
    <Permission route={route}>
      <BetaModule module={route.betaModule}>
        <Item id={`menu-item-${serializeMenuItemLabel(route.label)}`} as={NavLink} to={route.to} icon={route.icon || undefined}>
          {route.label}
        </Item>
      </BetaModule>
    </Permission>
  );
});

export { MenuRoute };
